import React from 'react';
import styled from 'styled-components';
import { device } from '../../constants/device-sizes';
import { getSiteName, isChatScapeMode } from '../../hooks/useSiteConfig';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background-color: #121212;
  color: #ffffff;
  min-height: 100vh;

  @media ${device.tablet} {
    padding: 3rem;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled.h1`
  font-size: var(--fs-h1);
  font-weight: var(--fw-bold);
  color: #ffffff;
  margin-bottom: 1rem;
`;

const Subtitle = styled.p`
  font-size: var(--fs-body);
  color: #cccccc;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
`;

const FAQSection = styled.div`
  margin-bottom: 2rem;
`;

const Question = styled.h2`
  font-size: var(--fs-h3);
  font-weight: var(--fw-bold);
  color: #ffffff;
  margin-bottom: 1rem;
  padding: 1rem 0;
  border-bottom: 2px solid #333333;
`;

const Answer = styled.div`
  font-size: var(--fs-body);
  color: #cccccc;
  line-height: 1.6;
  margin-bottom: 2rem;

  p {
    margin-bottom: 1rem;
  }

  ul {
    margin-left: 1.5rem;
    margin-bottom: 1rem;
  }

  li {
    margin-bottom: 0.5rem;
  }
`;

const CTASection = styled.div`
  text-align: center;
  margin-top: 4rem;
  padding: 2rem;
  background-color: #1e1e1e;
  border-radius: 8px;
`;

const CTAButton = styled.a`
  display: inline-block;
  background-color: var(--clr-purple);
  color: white;
  padding: 1rem 2rem;
  text-decoration: none;
  border-radius: 6px;
  font-weight: var(--fw-bold);
  font-size: var(--fs-body-bold);
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #b05dd6;
  }
`;

const getFaqData = () => {
  const siteName = getSiteName();
  const isChatScape = isChatScapeMode();

  const baseQuestions = [
    {
      question: 'How does anonymous chat work?',
      answer: `${siteName}'s anonymous chat rooms let you communicate without revealing your identity. You don't need to create an account, provide personal information, or use your real name. Simply enter a chat room and start talking with others who are also chatting anonymously.`,
    },
    {
      question: 'How quickly do messages disappear?',
      answer: `All messages in our anonymous chat rooms automatically disappear once they're sufficiently old. The chat interface only shows 30 messages on screen at a time.`,
    },
    {
      question: `What makes ${siteName} different from other anonymous chat apps?`,
      answer: isChatScape
        ? `${siteName} focuses on creating a chill, friendly environment for anonymous conversation and connection. We specifically ban creeps and toxic people, unlike many other chat sites.`
        : `${siteName} focuses on creating a chill, supportive environment for anonymous venting and casual conversation. We specifically ban creeps and toxic people, unlike many other chat sites.`,
    },
    {
      question: 'How do I report inappropriate behavior in anonymous chat?',
      answer: `When someone is being toxic, creepy, or anything of the sort, click on a message to display a report button. Our moderation team reviews reports quickly to keep anonymous chat rooms safe for everyone.`,
    },
    {
      question: 'Are there moderators in the anonymous chat rooms?',
      answer: `Yes, we have moderation systems in place to ensure our anonymous chat rooms remain respectful and supportive. While we preserve anonymity, we don't tolerate harassment, spam, or harmful content.`,
    },
    {
      question: 'Is anonymous chat completely free?',
      answer: `Yes, ${siteName}'s anonymous chat rooms are completely free to use.`,
    },
    {
      question: 'What is VentScape vs ChatScape?',
      answer: (
        <span>
          <a
            href='https://wwww.ventscape.life'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: 'var(--clr-purple)', textDecoration: 'underline' }}
          >
            VentScape
          </a>{' '}
          was a venting focused anonymous chat site, started in 2021. Over time,
          it became more chat oriented than venting oriented, so in order to
          continue building towards that use case, I made a clone of the site
          called{' '}
          <a
            href='https://wwww.chatscape.life'
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: 'var(--clr-purple)', textDecoration: 'underline' }}
          >
            ChatScape
          </a>
          . You can use the same account on both sites.
        </span>
      ),
    },
  ];

  // Only include ArchiveScape question for VentScape
  if (!isChatScape) {
    baseQuestions.push({
      question: 'Thoughts on ArchiveScape?',
      answer: `It's interesting, it's useful. Many people like it and use it, so generally I try to make features that help with the use cases people used it for. But overall I don't think most people consent to it so I've tried to keep it blocked for now.`,
    });
  }

  return baseQuestions;
};

function FAQ() {
  const siteName = getSiteName();
  const isChatScape = isChatScapeMode();
  const faqData = getFaqData();

  // FAQ Schema for rich results
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqData.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text:
          typeof faq.answer === 'string'
            ? faq.answer
            : 'VentScape was a venting focused anonymous chat site, started in 2021. Over time, it became more chat oriented than venting oriented, so in order to continue building towards that use case, I made a clone of the site called ChatScape. You can use the same account on both sites.',
      },
    })),
  };

  return (
    <Container>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      <Header>
        <Title>{siteName} Anonymous Chat FAQ</Title>
        <Subtitle>
          Everything you need to know about {siteName}'s anonymous chat rooms.
          Get answers about how anonymous chatting works, privacy protection,
          and more.
        </Subtitle>
      </Header>

      {faqData.map((faq, index) => (
        <FAQSection key={index}>
          <Question id={`faq-${index}`}>{faq.question}</Question>
          <Answer>
            <p>{faq.answer}</p>
          </Answer>
        </FAQSection>
      ))}

      <CTASection>
        <h3 style={{ marginBottom: '1rem', color: '#ffffff' }}>
          Ready to try {siteName}?
        </h3>
        <p style={{ marginBottom: '2rem', color: '#cccccc' }}>
          {isChatScape
            ? 'Join thousands of others in our friendly anonymous chat rooms'
            : 'Join thousands of others in our supportive anonymous chat rooms'}
        </p>
        <CTAButton href='/'>Start Chatting Anonymously</CTAButton>
      </CTASection>
    </Container>
  );
}

export default FAQ;
